// =============================================================================
// src/features/reputation/reputation-duel.ts
//
// Duelo 1-a-1 entre dois nomes do segmento (selects + barras + veredito).
// Porte FIEL de assets/reputation.js: pol 295, renderDuel 297-344, initDuel 346-362.
// Preserva: defaults ricardo/helena, anti-colisao de selects, ◂/▸ no gap, faixa do
//   lider e a frase de reacao do "perdedor" nas tres dimensoes.
//
// SRP: so o duelo. Reusa formatReputationNumber/reputationBand de reputation-data.
// =============================================================================

import {
  REPUTATION_DUEL_DIMS,
  REPUTATION_POLS,
  formatReputationNumber,
  reputationBand,
} from './reputation-data';
import type { ReputationDimKey, ReputationPol, ReputationPolId } from './reputation-data';

/** Acha um politico por id ou null — porte FIEL de `pol` (reputation.js 295). */
function findPol(id: string): ReputationPol | null {
  return REPUTATION_POLS.find((p) => p.id === id) ?? null;
}

/** Le o valor de uma dimensao de um politico (substitui o index dinamico a[d.key]). */
function dimValue(pol: ReputationPol, key: ReputationDimKey): number {
  return pol[key];
}

/** Renderiza barras + veredito do duelo — porte FIEL de `renderDuel` (reputation.js 297-344). */
export function renderReputationDuel(): void {
  const selA = document.getElementById('repDuelA') as HTMLSelectElement | null;
  const selB = document.getElementById('repDuelB') as HTMLSelectElement | null;
  const grid = document.getElementById('repDuelGrid');
  const verdict = document.getElementById('repDuelVerdict');
  if (!selA || !selB || !grid) return;
  const a = findPol(selA.value);
  const b = findPol(selB.value);
  if (!a || !b) return;

  grid.innerHTML = REPUTATION_DUEL_DIMS.map((d) => buildDuelMetric(d.key, d.label, d.tag, d.headline === true, a, b)).join('');
  if (verdict) verdict.innerHTML = buildDuelVerdict(a, b);
}

/** Monta uma linha de metrica do duelo — extrai o map de renderDuel (306-321). */
function buildDuelMetric(
  key: ReputationDimKey, label: string, tagLabel: string, headline: boolean, a: ReputationPol, b: ReputationPol,
): string {
  const av = dimValue(a, key);
  const bv = dimValue(b, key);
  const aLead = av > bv;
  const bLead = bv > av;
  const gap = Math.abs(av - bv);
  const mid = gap === 0 ? 'empate' : (aLead ? `◂ +${formatReputationNumber(gap)}` : `+${formatReputationNumber(gap)} ▸`);
  const tag = tagLabel ? `<span class="tag">${tagLabel}</span>` : '';
  return `<div class="rep-duel-metric${headline ? ' headline' : ''}">` +
    `<div class="rep-duel-mname">${label} ${tag}</div>` +
    '<div class="rep-duel-bars">' +
      `<div class="rep-duel-bar a"><span class="fill" style="width:${av}%;background:${a.color}"></span>` +
        `<span class="rep-duel-val${aLead ? ' lead' : ''}">${formatReputationNumber(av)}</span></div>` +
      `<div class="rep-duel-mid">${mid}</div>` +
      `<div class="rep-duel-bar b"><span class="fill" style="width:${bv}%;background:${b.color}"></span>` +
        `<span class="rep-duel-val${bLead ? ' lead' : ''}">${formatReputationNumber(bv)}</span></div>` +
    '</div></div>';
}

/** Rotulos longos das dimensoes secundarias do veredito (reputation.js 333). */
const DUEL_DIM_NAMES: Record<'nrs' | 'sov' | 'bps', string> = {
  nrs: 'Percepção (NRS)',
  sov: 'Presença (SOV)',
  bps: 'Protagonismo (BPS)',
};

/** Monta o texto do veredito — porte FIEL do bloco verdict de renderDuel (323-343). */
function buildDuelVerdict(a: ReputationPol, b: ReputationPol): string {
  const winner = a.index === b.index ? null : (a.index > b.index ? a : b);
  const loser = winner === a ? b : a;
  const parts: string[] = [];
  if (winner) {
    parts.push(`<b>${winner.name}</b> lidera o Reputation Index (${formatReputationNumber(winner.index)} vs ${formatReputationNumber(loser.index)}, faixa ${reputationBand(winner.index).name}).`);
  } else {
    parts.push(`<b>${a.name}</b> e <b>${b.name}</b> empatam no Reputation Index (${formatReputationNumber(a.index)}).`);
  }
  parts.push(buildDuelReaction(winner, a, b));
  return parts.join(' ');
}

/** Frase de reacao: onde o rival ainda vence, ou "vence em todas" — reputation.js 334-341. */
function buildDuelReaction(winner: ReputationPol | null, a: ReputationPol, b: ReputationPol): string {
  const rival = winner ? (winner === a ? b : a) : b;
  const champ = winner ?? a;
  const won = (['nrs', 'sov', 'bps'] as const).filter((k) => rival[k] > champ[k]);
  if (won.length) {
    return `${firstName(rival)} reage em ${won.map((k) => DUEL_DIM_NAMES[k]).join(' e ')}.`;
  }
  return `${firstName(champ)} vence em todas as três dimensões.`;
}

/** Primeiro nome (rival.name.split(' ')[0]) com guarda para noUncheckedIndexedAccess. */
function firstName(pol: ReputationPol): string {
  /* v8 ignore next -- String.split sempre retorna >=1 elemento; ?? pol.name defensivo p/ noUncheckedIndexedAccess */
  return pol.name.split(' ')[0] ?? pol.name;
}

/** Liga os selects e renderiza o duelo — porte FIEL de `initDuel` (reputation.js 346-362). */
export function initReputationDuel(): void {
  const selA = document.getElementById('repDuelA') as HTMLSelectElement | null;
  const selB = document.getElementById('repDuelB') as HTMLSelectElement | null;
  if (!selA || !selB) return;
  if (selA.options.length) {
    renderReputationDuel();
    return;
  }
  const opts = REPUTATION_POLS.map((p) => `<option value="${p.id}">${p.name} · ${p.party}</option>`).join('');
  selA.innerHTML = opts;
  selB.innerHTML = opts;
  selA.value = 'ricardo';
  selB.value = 'omar';
  bindDuelAntiCollision(selA, selB);
  bindDuelAntiCollision(selB, selA);
  renderReputationDuel();
}

/** Impede que os dois lados escolham o mesmo nome — porte de reputation.js 353-360. */
function bindDuelAntiCollision(changed: HTMLSelectElement, other: HTMLSelectElement): void {
  changed.addEventListener('change', () => {
    if (changed.value === other.value) {
      const fallback = REPUTATION_POLS.find((p) => p.id !== (changed.value as ReputationPolId));
      if (fallback) other.value = fallback.id;
    }
    renderReputationDuel();
  });
}
